import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Award, ArrowLeft, Printer, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function CertificateView() {
  const { id } = useParams();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/ai/certificates/${id}`)
      .then((r) => setCert(r.data))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      </div>
    );
  }
  if (!cert) {
    return (
      <div className="max-w-xl mx-auto py-24 text-center">
        <h2 className="text-2xl font-bold">Certificate not found</h2>
        <Link to="/certificates" className="text-primary mt-4 inline-block">
          ← Back to certificates
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <Button asChild variant="ghost" size="sm">
          <Link to="/certificates" data-testid="certificate-back-link">
            <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
          </Link>
        </Button>
        <Button
          onClick={() => window.print()}
          className="rounded-full"
          data-testid="certificate-print-button"
        >
          <Printer className="h-4 w-4 mr-1.5" /> Print / Save PDF
        </Button>
      </div>

      <div
        className="relative rounded-3xl border-[12px] border-double border-amber-200 dark:border-amber-800/40 bg-white dark:bg-slate-950 shadow-2xl p-10 lg:p-16 print:shadow-none print:border-amber-200"
        data-testid="certificate-canvas"
      >
        {/* corner flourishes */}
        <div className="absolute top-6 left-6 h-10 w-10 border-l-2 border-t-2 border-amber-400 dark:border-amber-600" />
        <div className="absolute top-6 right-6 h-10 w-10 border-r-2 border-t-2 border-amber-400 dark:border-amber-600" />
        <div className="absolute bottom-6 left-6 h-10 w-10 border-l-2 border-b-2 border-amber-400 dark:border-amber-600" />
        <div className="absolute bottom-6 right-6 h-10 w-10 border-r-2 border-b-2 border-amber-400 dark:border-amber-600" />

        <div className="text-center">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-amber-100 dark:bg-amber-900/60 mb-5">
            <Award className="h-8 w-8 text-amber-600 dark:text-amber-300" />
          </div>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
            Tutors Paradise
          </p>
          <h1 className="font-heading text-4xl lg:text-5xl font-bold tracking-tight mt-2 text-slate-900 dark:text-slate-100">
            Certificate of Completion
          </h1>

          <p className="mt-10 text-sm uppercase tracking-wider text-muted-foreground">
            This certificate is proudly presented to
          </p>
          <p className="font-heading text-3xl lg:text-4xl font-semibold mt-3 text-slate-900 dark:text-slate-100">
            {cert.student_name || "Learner"}
          </p>

          <p className="mt-7 text-muted-foreground leading-relaxed max-w-xl mx-auto">
            for successfully completing the module
            <span className="block font-semibold text-foreground text-lg mt-1">
              “{cert.module_title}”
            </span>
            in <span className="font-medium text-foreground">{cert.subject}</span>, with a passing
            score of <span className="font-bold text-foreground">{cert.score}%</span>.
          </p>

          <div className="mt-10 grid grid-cols-2 gap-6 max-w-md mx-auto text-left">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Issued on</p>
              <p className="font-medium">{new Date(cert.issued_at).toLocaleDateString(undefined, {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Certificate ID</p>
              <p className="font-mono text-sm">{cert.code}</p>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-dashed border-amber-200 dark:border-amber-900/50 text-xs text-muted-foreground">
            Verified by Tutors Paradise · AI-assessed module ·{" "}
            {cert.model ? cert.model.toUpperCase() : "AI"}
          </div>
        </div>
      </div>
    </div>
  );
}
